import React, { useState } from 'react';
import { 
  Building2, GraduationCap, Phone, Mail, Award, Lock, ExternalLink, 
  Menu, X, ChevronDown, ChevronRight, CheckCircle2, ShieldCheck, Sparkles, BookOpen, 
  FileText, MapPin, Calendar, Layers, ArrowLeft, User
} from 'lucide-react';
import { INSTITUTION_INFO } from '../data/portalData';
import { SocialIconLinks } from './SocialLinks';
import { CandidateAccountDrawer } from './LMS/CandidateAccountDrawer';

const CORE_PROGRAMMES_SUBMENU = [
  {
    id: 'phd-ba',
    code: 'PhD BA',
    name: 'PhD in Business Administration',
    level: 'Terminal Doctorate',
    duration: '3 - 5 Years'
  },
  {
    id: 'dba',
    code: 'DBA',
    name: 'Doctor of Business Administration',
    level: 'Executive Practice Doctorate',
    duration: '3 Years'
  },
  {
    id: 'mba',
    code: 'MBA',
    name: 'Master of Business Administration',
    level: 'Executive Master',
    duration: '18 - 24 Mos'
  },
  {
    id: 'pgd-ba',
    code: 'PGD',
    name: 'Postgraduate Diploma (PGD)',
    level: 'Executive Bridging Pathway',
    duration: '12 - 18 Mos'
  },
  {
    id: 'dpa',
    code: 'DPA',
    name: 'Doctor of Public Administration',
    level: 'Public Leadership Doctorate',
    duration: '36 Months'
  },
  {
    id: 'phd-af',
    code: 'PhD AF',
    name: 'Doctor of Accounting & Finance',
    level: 'Senior Financial Doctorate',
    duration: '3 - 5 Years'
  }
];

export const HeaderNav = ({ 
  activeView, 
  setActiveView, 
  selectedStudent, 
  setSelectedStudent, 
  onSelectProgramme 
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lmsDropdownOpen, setLmsDropdownOpen] = useState(false);
  const [programmesDropdownOpen, setProgrammesDropdownOpen] = useState(false);
  const [candidateDrawerOpen, setCandidateDrawerOpen] = useState(false);
  const [publicLmsDropdownOpen, setPublicLmsDropdownOpen] = useState(false);

  const handleProgrammeClick = (progId) => {
    setProgrammesDropdownOpen(false);
    setMobileMenuOpen(false);
    if (onSelectProgramme) {
      onSelectProgramme(progId);
    } else {
      setActiveView('programme-detail');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Standalone dedicated portal authentication pages render their own specialized headers
  if (activeView === 'lms-hub' || (activeView && activeView.startsWith('lms-login'))) {
    return null;
  }

  const isLmsView = activeView && activeView.startsWith('lms');

  // Minimal executive header for LMS views to maximize viewport content space
  if (isLmsView) {
    return (
      <header className="sticky top-0 z-50 bg-[#06182B] text-white border-b-2 border-[#7A470E] shadow-lg print:hidden">
        <div className="w-full px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
          {/* Left: Compact Brand & Return to Public Portal */}
          <div className="flex items-center space-x-3 shrink-0">
            <button
              onClick={() => setActiveView('home')}
              className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-xl bg-[#0B2644] hover:bg-[#7A470E] text-white text-xs font-bold font-sc transition-colors border border-slate-700/80 cursor-pointer shadow-xs group"
              title="Return to Public Portal Home"
            >
              <ArrowLeft className="w-3.5 h-3.5 text-amber-300 group-hover:-translate-x-0.5 transition-transform" />
              <span className="hidden sm:inline">Portal Home</span>
            </button>

            <div 
              onClick={() => setActiveView('home')}
              className="flex items-center space-x-2.5 cursor-pointer group"
              title="AKSU Business School - Digital Campus"
            >
              <img 
                src="/Logo.png" 
                alt="AKSU Business School Crest" 
                className="w-8 h-8 object-contain logo-glow shrink-0" 
              />
              <div className="flex flex-col">
                <span className="font-sc font-black text-xs sm:text-sm text-white tracking-wide group-hover:text-amber-300 transition-colors leading-tight">
                  AKSU Business School
                </span>
                <span className="text-[10px] text-amber-300/90 font-mono tracking-wider">
                  Digital Campus · LMS
                </span>
              </div>
            </div>
          </div>

          {/* Center: Current Workspace Context Badge */}
          <div className="hidden md:flex items-center space-x-2 bg-[#030F1C]/80 px-3.5 py-1.5 rounded-full border border-slate-800 text-xs">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="font-sc font-bold text-amber-200">
              {activeView === 'lms-director' ? 'Executive Director Console · Global Admin' :
               activeView === 'lms-manager' ? 'Global Content Manager · Courseware Repository' :
               activeView === 'lms-faculty' ? 'Faculty Portal · Module & Seminar Desk' :
               activeView === 'lms-login' ? 'LMS Authentication Desk · RBAC Gate' :
               'Executive Scholar LMS Workspace'}
            </span>
            <span className="text-slate-500 font-mono text-[10px]">| 2026/2027</span>
          </div>

          {/* Right: Quick LMS Role Switcher Dropdown & Exit LMS */}
          <div className="flex items-center space-x-2 shrink-0">
            <div className="relative">
              <button
                onClick={() => setLmsDropdownOpen(!lmsDropdownOpen)}
                className="px-3 py-1.5 rounded-xl bg-[#0B2644] hover:bg-[#133562] text-white text-xs font-bold font-sc flex items-center space-x-1.5 border border-[#7A470E]/50 cursor-pointer shadow-xs transition-colors"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-amber-300" />
                <span className="hidden sm:inline">Switch Console</span>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-300 transition-transform ${lmsDropdownOpen ? 'rotate-180 text-amber-300' : ''}`} />
              </button>

              {lmsDropdownOpen && (
                <div 
                  className="absolute right-0 mt-2 w-[calc(100vw-2rem)] max-w-xs sm:max-w-none sm:w-88 bg-white rounded-2xl shadow-2xl border-2 border-slate-200 py-2 z-50 text-slate-900 animate-in fade-in slide-in-from-top-2 duration-150 max-h-[85vh] overflow-y-auto"
                  onMouseLeave={() => setLmsDropdownOpen(false)}
                >
                  <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
                    <span className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc">
                      Executive LMS Workspaces
                    </span>
                    <span className="text-[10px] font-mono text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-bold">
                      Active
                    </span>
                  </div>

                  {/* Login Desk */}
                  <button
                    onClick={() => {
                      setActiveView('lms-login');
                      setLmsDropdownOpen(false);
                    }}
                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50 flex items-center justify-between text-slate-700 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="w-7 h-7 rounded-lg bg-[#06182B] text-amber-400 flex items-center justify-center shrink-0">
                        <Lock className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <div className="text-xs font-bold font-sc text-[#0B2644]">LMS Portal Login Desk</div>
                        <div className="text-[10px] text-slate-500">Sign in with assigned RBAC credentials</div>
                      </div>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                  </button>

                  {/* Director Console */}
                  <button
                    onClick={() => {
                      setActiveView('lms-director');
                      setLmsDropdownOpen(false);
                    }}
                    className="w-full text-left px-3.5 py-2 hover:bg-amber-50/50 flex items-center justify-between text-slate-700 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#0B2644] to-[#030F1C] border border-[#B87A28]/60 text-amber-300 flex items-center justify-center shrink-0">
                        <ShieldCheck className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-bold font-sc text-[#0B2644]">Director Dashboard (Global Admin)</div>
                    </div>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-[#7A470E] border border-amber-200 font-sc">Directorate</span>
                  </button>

                  {/* Global Content Manager */}
                  <button
                    onClick={() => {
                      setActiveView('lms-manager');
                      setLmsDropdownOpen(false);
                    }}
                    className="w-full text-left px-3.5 py-2 hover:bg-cyan-50/40 flex items-center justify-between text-slate-700 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#082038] to-[#051628] border border-cyan-400/50 text-cyan-300 flex items-center justify-center shrink-0">
                        <Layers className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-bold font-sc text-[#0B2644]">LMS Global Content Manager</div>
                    </div>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-900 border border-cyan-200 font-sc">CMS Admin</span>
                  </button>

                  {/* Faculty Portal */}
                  <button
                    onClick={() => {
                      setActiveView('lms-faculty');
                      setLmsDropdownOpen(false);
                    }}
                    className="w-full text-left px-3.5 py-2 hover:bg-amber-50/40 flex items-center justify-between text-slate-700 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#7A470E] to-[#3B1E02] border border-amber-300/50 text-amber-200 flex items-center justify-center shrink-0">
                        <BookOpen className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-bold font-sc text-[#0B2644]">Faculty Portal (Scoped Access)</div>
                    </div>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-[#7A470E] border border-amber-200 font-sc">Faculty</span>
                  </button>

                  {/* Student Portals Header */}
                  <div className="px-4 py-1.5 border-t border-b border-slate-100 flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-[#0B2644] bg-slate-50 font-sc">
                    <span>Student LMS Portals</span>
                    <span className="text-[10px] text-slate-400 font-normal">Semester Tracks</span>
                  </div>

                  {/* Student 1: DBA */}
                  <button
                    onClick={() => {
                      setSelectedStudent('student-dba');
                      setActiveView('lms-student');
                      setLmsDropdownOpen(false);
                    }}
                    className="w-full text-left px-3.5 py-2 hover:bg-amber-50/60 flex items-center justify-between text-slate-700 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="w-6 h-6 rounded-lg bg-amber-500/10 border border-amber-300 text-[#7A470E] flex items-center justify-center shrink-0 group-hover:bg-[#7A470E] group-hover:text-white transition-all">
                        <Award className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-xs font-semibold">1. DBA Student Portal (BUS 901 - 941)</span>
                    </div>
                    <span className="text-[10px] bg-amber-100 text-[#5C3306] font-bold px-1.5 py-0.5 rounded font-mono">DBA</span>
                  </button>

                  {/* Student 2: MAF */}
                  <button
                    onClick={() => {
                      setSelectedStudent('student-maf');
                      setActiveView('lms-student');
                      setLmsDropdownOpen(false);
                    }}
                    className="w-full text-left px-3.5 py-2 hover:bg-emerald-50/60 flex items-center justify-between text-slate-700 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="w-6 h-6 rounded-lg bg-emerald-500/10 border border-emerald-300 text-emerald-800 flex items-center justify-center shrink-0 group-hover:bg-emerald-800 group-hover:text-white transition-all">
                        <Building2 className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-xs font-semibold">2. MAF Student Portal (MAF & ACC 800)</span>
                    </div>
                    <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded font-mono">MAF</span>
                  </button>

                  {/* Student 3: DAF */}
                  <button
                    onClick={() => {
                      setSelectedStudent('student-daf');
                      setActiveView('lms-student');
                      setLmsDropdownOpen(false);
                    }}
                    className="w-full text-left px-3.5 py-2 hover:bg-purple-50/60 flex items-center justify-between text-slate-700 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="w-6 h-6 rounded-lg bg-purple-500/10 border border-purple-300 text-[#5C3306] flex items-center justify-center shrink-0 group-hover:bg-[#5C3306] group-hover:text-white transition-all">
                        <Award className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-xs font-semibold">3. DAF Fellow Portal (DAF 901 - 941)</span>
                    </div>
                    <span className="text-[10px] bg-purple-100 text-[#5C3306] font-bold px-1.5 py-0.5 rounded font-mono">DAF</span>
                  </button>

                  {/* Student 4: PhD */}
                  <button
                    onClick={() => {
                      setSelectedStudent('student-phd');
                      setActiveView('lms-student');
                      setLmsDropdownOpen(false);
                    }}
                    className="w-full text-left px-3.5 py-2 hover:bg-blue-50/60 flex items-center justify-between text-slate-700 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="w-6 h-6 rounded-lg bg-blue-500/10 border border-blue-300 text-[#0B2644] flex items-center justify-center shrink-0 group-hover:bg-[#0B2644] group-hover:text-white transition-all">
                        <GraduationCap className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-xs font-semibold">4. PhD Research Portal</span>
                    </div>
                    <span className="text-[10px] bg-blue-100 text-[#0B2644] font-bold px-1.5 py-0.5 rounded font-mono">PhD</span>
                  </button>

                  {/* Student 5: PGD */}
                  <button
                    onClick={() => {
                      setSelectedStudent('student-pgd');
                      setActiveView('lms-student');
                      setLmsDropdownOpen(false);
                    }}
                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50 flex items-center justify-between text-slate-700 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="w-6 h-6 rounded-lg bg-slate-500/10 border border-slate-300 text-slate-800 flex items-center justify-center shrink-0 group-hover:bg-slate-800 group-hover:text-white transition-all">
                        <FileText className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-xs font-semibold">5. PGD Bridging Portal</span>
                    </div>
                    <span className="text-[10px] bg-slate-100 text-slate-800 font-bold px-1.5 py-0.5 rounded font-mono">PGD</span>
                  </button>
                </div>
              )}
            </div>

            {/* Candidate Account Trigger on Student LMS view */}
            {activeView === 'lms-student' && (
              <button
                onClick={() => setCandidateDrawerOpen(true)}
                className="px-2.5 py-1.5 rounded-xl bg-[#0B2644] hover:bg-[#133562] text-white text-xs font-bold font-sc flex items-center space-x-1.5 border border-amber-400/50 cursor-pointer shadow-xs transition-colors"
                title="Open Candidate Account Menu (Profile, Bursary, Grades, ID Pass)"
              >
                <User className="w-3.5 h-3.5 text-amber-300" />
                <span className="hidden sm:inline">Candidate Account</span>
              </button>
            )}

            {/* Exit LMS Button */}
            <button
              onClick={() => setActiveView('home')}
              className="px-3 py-1.5 rounded-xl bg-[#7A470E] hover:bg-[#5C3306] text-white text-xs font-bold font-sc transition-colors cursor-pointer shadow-xs flex items-center space-x-1"
              title="Return to AKSU Business School Website"
            >
              <span>Exit LMS</span>
            </button>
          </div>
        </div>

        {/* Candidate Account Drawer with 6 Submenus */}
        <CandidateAccountDrawer 
          isOpen={candidateDrawerOpen}
          onClose={() => setCandidateDrawerOpen(false)}
          studentId={selectedStudent}
          onSelectStudent={setSelectedStudent}
          onLogout={() => setActiveView('home')}
        />
      </header>
    );
  }

  // Hide top security and contact strip on Admissions portal per institutional preference
  const isAdmissionsPage = activeView === 'apply' || activeView === 'admissions' || (typeof window !== 'undefined' && window.location.pathname.includes('/admissions'));

  return (
    <header className="relative z-40 bg-white shadow-sm print:hidden">
      
      {/* ======================================================== */}
      {/* 1. TOPMOST OFFICIAL SECURITY & CONTACT STRIP             */}
      {/* ======================================================== */}
      {!isAdmissionsPage && (
        <div className="bg-[#06182B] text-white text-xs py-2 px-4 border-b-2 border-[#7A470E]">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
            {/* Rebalanced Official Institutional Context */}
            <div className="flex items-center space-x-2 text-xs">
              <span className="flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-[#030F1C] text-amber-300 border border-[#7A470E]/50 font-sc">
                2025/2026 Academic Session
              </span>
              <span className="text-[#7A470E]/50">•</span>
              <span className="text-slate-200 font-medium font-sc">
                Obio Akpa Campus · Uyo Liaison Office
              </span>
              <span className="hidden lg:inline-block text-[#7A470E]/50">•</span>
              <span className="hidden lg:inline-block text-emerald-400 font-bold font-sc">
                Hybrid Executive Delivery
              </span>
            </div>

            {/* Quick Contact & Accreditation Signals */}
            <div className="flex items-center space-x-4 text-[11px]">
              <a 
                href={`https://wa.me/2348033380401`} 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center hover:text-[#B87A28] transition-colors"
              >
                <Phone className="w-3 h-3 mr-1 text-[#7A470E]" />
                <span className="font-mono">{INSTITUTION_INFO.contacts.phoneWhatsApp}</span>
              </a>
              <a 
                href={`mailto:${INSTITUTION_INFO.contacts.generalEmail}`} 
                className="hidden md:flex items-center hover:text-[#B87A28] transition-colors"
              >
                <Mail className="w-3 h-3 mr-1 text-[#7A470E]" />
                <span>{INSTITUTION_INFO.contacts.generalEmail}</span>
              </a>
              <div className="hidden lg:flex items-center text-slate-300 pl-2 border-l border-slate-700">
                <ShieldCheck className="w-3.5 h-3.5 mr-1 text-[#7A470E]" />
                <span className="font-sc">NUC Accredited</span>
              </div>
              <div className="flex items-center space-x-1.5 pl-3 border-l border-slate-700/80">
                <span className="text-[10px] text-slate-400 font-sc hidden sm:inline">Follow:</span>
                <SocialIconLinks theme="header" iconClassName="w-3.5 h-3.5" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 2. CENTRE BRAND HEADER (Logo & Associated Text in Centre) */}
      {/* ======================================================== */}
      <div className="bg-white border-b border-slate-200 py-3.5 sm:py-5 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          
          {/* Left Metadata / Session Stamp (Desktop only) */}
          <div className="hidden lg:flex flex-col items-start text-xs space-y-1 w-64 shrink-0">
            <span className="text-[11px] font-bold text-[#7A470E] uppercase tracking-wider font-sc">
              Executive Academic Intake
            </span>
            <div className="font-bold text-[#0B2644] font-sc text-sm">
              2025/2026 Academic Session
            </div>
            <div className="flex items-center text-slate-500 text-[11px]">
              <MapPin className="w-3.5 h-3.5 mr-1 text-[#7A470E]" />
              <span>Obio Akpa Campus & Uyo Liaison</span>
            </div>
          </div>

          {/* CENTRE: Brand Logo & Associated Text (Centred Exactly like Reference) */}
          <div 
            onClick={() => setActiveView('home')} 
            className="flex flex-col items-center justify-center text-center cursor-pointer group select-none mx-auto"
          >
            {/* Logo Crest */}
            <div className="relative mb-1">
              <img 
                src="/Logo.png" 
                alt="Akwa Ibom State University Official Crest" 
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain logo-glow select-none"
              />
              <div className="absolute -bottom-1 -right-1 bg-[#7A470E] text-white rounded-full p-0.5 shadow">
                <CheckCircle2 className="w-3.5 h-3.5" />
              </div>
            </div>

            {/* University & Business School Title */}
            <div className="flex flex-col items-center">
              <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-bold text-[#0B2644] font-sc">
                Akwa Ibom State University
              </span>
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-wide text-[#7A470E] font-sc leading-tight group-hover:text-[#5C3306] transition-colors">
                BUSINESS SCHOOL
              </h1>
              <p className="text-[11px] sm:text-xs font-bold tracking-[0.3em] uppercase text-[#0B2644] font-sc mt-0.5">
                Knowledge · Enterprise · Leadership
              </p>
            </div>
          </div>

          {/* Right Action CTAs: Apply Now in Gold & LMS Portals in Navy */}
          <div className="hidden sm:flex items-center justify-end space-x-3 w-64 shrink-0 whitespace-nowrap">
            {/* Unified Portal Login Button */}
            <button
              onClick={() => {
                setActiveView('lms-hub');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-4 py-2.5 text-xs sm:text-sm font-bold rounded-xl transition-all flex items-center space-x-2 bg-gradient-to-r from-[#030F1C] to-[#0B2644] hover:from-[#7A470E] hover:to-[#5C3306] text-white hover:text-amber-300 border border-[#7A470E] shadow-sm hover:shadow-md cursor-pointer font-sc group"
              title="Access Central Portal Authentication Hub"
            >
              <Lock className="w-4 h-4 text-amber-400 group-hover:text-amber-300 transition-colors" />
              <span>Portal Login</span>
            </button>
          </div>

          {/* Mobile Menu Button on smaller screens */}
          <div className="flex lg:hidden items-center space-x-2">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-slate-700 hover:text-[#0B2644] hover:bg-slate-100 cursor-pointer"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

        </div>
      </div>

      {/* ======================================================== */}
      {/* 3. MAIN HORIZONTAL NAVIGATION BAR (Horizontal Full Width) */}
      {/* ======================================================== */}
      <nav className="hidden lg:block bg-white border-b-2 border-slate-200">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex items-center justify-center space-x-1 sm:space-x-2 lg:space-x-3 xl:space-x-5 py-0">
            
            {/* Home View */}
            <button 
              onClick={() => setActiveView('home')}
              className={`py-3 px-2 sm:px-2.5 lg:px-3 text-xs sm:text-sm font-black font-sc uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer flex items-center border-b-4 -mb-[2px] ${
                activeView === 'home' 
                  ? 'border-[#7A470E] text-[#0B2644]' 
                  : 'border-transparent text-slate-700 hover:text-[#0B2644] hover:border-slate-300'
              }`}
            >
              <span>Home</span>
            </button>

            {/* Admission Advert */}
            <button 
              onClick={() => setActiveView('admission-advert')}
              className={`py-3 px-2 sm:px-2.5 lg:px-3 text-xs sm:text-sm font-black font-sc uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer flex items-center space-x-1.5 border-b-4 -mb-[2px] ${
                activeView === 'admission-advert' 
                  ? 'border-[#7A470E] text-[#0B2644]' 
                  : 'border-transparent text-slate-700 hover:text-[#0B2644] hover:border-slate-300'
              }`}
            >
              <FileText className="w-3.5 h-3.5 text-[#7A470E] shrink-0" />
              <span>Admission Advert</span>
              <span className="text-[10px] bg-amber-100 text-[#0B2644] px-1 py-0.2 rounded font-mono font-black">
                2025/26
              </span>
            </button>

            {/* Official Class Timetable */}
            <button 
              onClick={() => setActiveView('class-timetable')}
              className={`py-3 px-2 sm:px-2.5 lg:px-3 text-xs sm:text-sm font-black font-sc uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer flex items-center space-x-1.5 border-b-4 -mb-[2px] ${
                activeView === 'class-timetable' 
                  ? 'border-[#7A470E] text-[#0B2644]' 
                  : 'border-transparent text-slate-700 hover:text-[#0B2644] hover:border-slate-300'
              }`}
            >
              <Calendar className="w-3.5 h-3.5 text-[#7A470E] shrink-0" />
              <span>Class Timetable</span>
              <span className="text-[10px] bg-emerald-100 text-emerald-900 px-1 py-0.2 rounded font-mono font-black">
                Pioneer Cohort
              </span>
            </button>

            {/* Academic Programmes with Dedicated Pages Submenu */}
            <div 
              className="relative"
              onMouseEnter={() => setProgrammesDropdownOpen(true)}
              onMouseLeave={() => setProgrammesDropdownOpen(false)}
            >
              <button
                onClick={() => setProgrammesDropdownOpen(!programmesDropdownOpen)}
                className={`py-3 px-2 sm:px-2.5 lg:px-3 text-xs sm:text-sm font-black font-sc uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer flex items-center space-x-1 border-b-4 -mb-[2px] ${
                  activeView === 'programme-detail'
                    ? 'border-[#7A470E] text-[#0B2644]'
                    : 'border-transparent text-slate-700 hover:text-[#0B2644] hover:border-slate-300'
                }`}
              >
                <span>Programmes</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${programmesDropdownOpen ? 'rotate-180 text-[#7A470E]' : 'opacity-70'}`} />
              </button>

              {/* Programmes Dedicated Pages Floating Submenu */}
              {programmesDropdownOpen && (
                <div 
                  className="absolute left-0 mt-0 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border-2 border-slate-200 py-3 px-2.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150"
                >
                  <div className="px-2.5 pb-2 mb-2 border-b border-slate-100 flex items-center justify-between">
                    <span className="text-[11px] font-black uppercase tracking-wider text-[#7A470E] font-sc">
                      Programmes & Admissions
                    </span>
                    <span className="text-[10px] font-mono text-slate-400 font-bold">2025/2026</span>
                  </div>

                  {/* 1. Requirements Placed First Under Programmes */}
                  <div className="mb-2 pb-2 border-b border-slate-100">
                    <button
                      onClick={() => {
                        setProgrammesDropdownOpen(false);
                        if (activeView !== 'home') {
                          setActiveView('home');
                        }
                        setTimeout(() => {
                          document.getElementById('admissions')?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                      className="w-full text-left p-2.5 rounded-xl bg-amber-50/90 hover:bg-amber-100/90 border border-[#7A470E]/30 hover:border-[#7A470E] transition-all flex items-center justify-between group cursor-pointer shadow-xs"
                    >
                      <div className="flex items-center space-x-2.5">
                        <div className="w-10 h-8 rounded-lg bg-[#7A470E] text-white flex items-center justify-center shrink-0">
                          <CheckCircle2 className="w-4 h-4 text-amber-200" />
                        </div>
                        <div>
                          <div className="text-xs sm:text-sm font-black text-[#0B2644] font-sc leading-tight group-hover:text-[#7A470E] transition-colors flex items-center space-x-1.5">
                            <span>Admission Requirements</span>
                            <span className="text-[9px] bg-[#0B2644] text-amber-300 px-1.5 py-0.2 rounded font-mono font-bold uppercase">All Tracks</span>
                          </div>
                          <div className="text-[10px] text-slate-600 font-sans">
                            Entry benchmarks, qualifications & degree criteria
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#7A470E] group-hover:translate-x-0.5 transition-all shrink-0" />
                    </button>
                  </div>

                  {/* 2. Scholar Registration Placed Under Programmes */}
                  <div className="mb-2 pb-2 border-b border-slate-100">
                    <button
                      onClick={() => {
                        setProgrammesDropdownOpen(false);
                        setActiveView('enrolment');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full text-left p-2.5 rounded-xl bg-blue-50/70 hover:bg-blue-100/70 border border-[#0B2644]/20 hover:border-[#0B2644] transition-all flex items-center justify-between group cursor-pointer shadow-xs"
                    >
                      <div className="flex items-center space-x-2.5">
                        <div className="w-10 h-8 rounded-lg bg-[#0B2644] text-white flex items-center justify-center shrink-0">
                          <GraduationCap className="w-4 h-4 text-amber-300" />
                        </div>
                        <div>
                          <div className="text-xs sm:text-sm font-black text-[#0B2644] font-sc leading-tight group-hover:text-[#7A470E] transition-colors flex items-center space-x-1.5">
                            <span>Scholar Registration Desk</span>
                            <span className="text-[9px] bg-[#7A470E] text-white px-1.5 py-0.2 rounded font-mono font-bold uppercase">Enrolment</span>
                          </div>
                          <div className="text-[10px] text-slate-600 font-sans">
                            Matriculation pass, course confirmation & digital ID
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#0B2644] group-hover:translate-x-0.5 transition-all shrink-0" />
                    </button>
                  </div>

                  <div className="space-y-1">
                    {CORE_PROGRAMMES_SUBMENU.map((prog) => (
                      <button
                        key={prog.id}
                        onClick={() => handleProgrammeClick(prog.id)}
                        className="w-full text-left p-2 rounded-xl hover:bg-amber-50/80 border border-transparent hover:border-[#7A470E]/30 transition-all flex items-center justify-between group cursor-pointer"
                      >
                        <div className="flex items-center space-x-2.5">
                          <div className="w-12 h-7 rounded-lg bg-[#0B2644] text-white flex items-center justify-center font-black text-[10px] font-sc group-hover:bg-[#7A470E] transition-colors shrink-0">
                            {prog.code}
                          </div>
                          <div>
                            <div className="text-xs sm:text-sm font-bold text-[#0B2644] font-sc leading-tight group-hover:text-[#7A470E] transition-colors">
                              {prog.name}
                            </div>
                            <div className="text-[10px] text-slate-500 font-sans">
                              {prog.level} · {prog.duration}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#7A470E] group-hover:translate-x-0.5 transition-all shrink-0" />
                      </button>
                    ))}
                  </div>

                  {/* Bottom quick link to full catalogue */}
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <button
                      onClick={() => {
                        setProgrammesDropdownOpen(false);
                        if (activeView !== 'home') {
                          setActiveView('home');
                        }
                        setTimeout(() => {
                          document.getElementById('programmes')?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                      className="w-full py-2 px-3 rounded-lg text-xs font-bold text-[#0B2644] hover:bg-slate-100 font-sc text-center flex items-center justify-center space-x-1 cursor-pointer transition-colors"
                    >
                      <BookOpen className="w-3.5 h-3.5 text-[#7A470E]" />
                      <span>View Full Catalogue on Home</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* LMS Menu in Header Navbar */}
            <div 
              className="relative"
              onMouseEnter={() => setPublicLmsDropdownOpen(true)}
              onMouseLeave={() => setPublicLmsDropdownOpen(false)}
            >
              <button
                onClick={() => {
                  setPublicLmsDropdownOpen(false);
                  setActiveView('lms-student');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`py-3 px-2 sm:px-2.5 lg:px-3 text-xs sm:text-sm font-black font-sc uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer flex items-center space-x-1 border-b-4 -mb-[2px] ${
                  activeView && activeView.startsWith('lms')
                    ? 'border-[#7A470E] text-[#0B2644]'
                    : 'border-transparent text-slate-700 hover:text-[#0B2644] hover:border-slate-300'
                }`}
              >
                <GraduationCap className="w-3.5 h-3.5 text-[#7A470E] shrink-0" />
                <span>LMS</span>
                <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1 py-0.2 rounded font-mono font-black">
                  Active
                </span>
                <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${publicLmsDropdownOpen ? 'rotate-180 text-[#7A470E]' : 'opacity-70'}`} />
              </button>

              {/* LMS Dropdown */}
              {publicLmsDropdownOpen && (
                <div className="absolute left-0 mt-0 w-84 bg-white rounded-2xl shadow-2xl border-2 border-slate-200 py-3 px-2.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                  <div className="px-2.5 pb-2 mb-2 border-b border-slate-100 flex items-center justify-between">
                    <span className="text-[11px] font-black uppercase tracking-wider text-[#7A470E] font-sc">
                      Executive LMS Workspace
                    </span>
                    <span className="text-[10px] font-mono text-emerald-600 font-bold">● Scholar Access</span>
                  </div>

                  <div className="space-y-1">
                    {/* Student LMS */}
                    <button
                      onClick={() => {
                        setPublicLmsDropdownOpen(false);
                        setActiveView('lms-student');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full text-left p-2.5 rounded-xl bg-gradient-to-r from-amber-50 to-amber-100/60 hover:from-amber-100 hover:to-amber-200/60 border border-[#7A470E]/30 transition-all flex items-center justify-between group cursor-pointer shadow-xs"
                    >
                      <div className="flex items-center space-x-2.5">
                        <div className="w-9 h-8 rounded-lg bg-[#0B2644] text-white flex items-center justify-center font-black text-xs shrink-0 group-hover:bg-[#7A470E] transition-colors">
                          <GraduationCap className="w-4 h-4 text-amber-300" />
                        </div>
                        <div>
                          <div className="text-xs sm:text-sm font-bold text-[#0B2644] font-sc leading-tight group-hover:text-[#7A470E] transition-colors">
                            Student LMS & Scholar Registration
                          </div>
                          <div className="text-[10px] text-slate-500 font-sans">
                            Tier 3 · DBA, PhD, MBA & PGD scholars
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#7A470E] group-hover:translate-x-0.5 transition-all shrink-0" />
                    </button>

                    {/* Central LMS Login Hub */}
                    <button
                      onClick={() => {
                        setPublicLmsDropdownOpen(false);
                        setActiveView('lms-hub');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full text-left p-2 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center space-x-2.5">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center text-xs shrink-0 group-hover:bg-[#0B2644] group-hover:text-white transition-colors">
                          <Lock className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-[#0B2644] font-sc group-hover:text-[#7A470E] transition-colors">
                            Central LMS Login Hub
                          </div>
                          <div className="text-[10px] text-slate-500 font-sans">
                            Switch role personas & access gates
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#7A470E] transition-all shrink-0" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Director's Desk */}
            <a 
              href="#director" 
              onClick={(e) => {
                if (activeView !== 'home') {
                  e.preventDefault();
                  setActiveView('home');
                  setTimeout(() => {
                    document.getElementById('director')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }
              }}
              className="py-3 px-2 sm:px-2.5 lg:px-3 text-xs sm:text-sm font-black font-sc uppercase tracking-wider transition-all whitespace-nowrap text-slate-700 hover:text-[#0B2644] hover:border-slate-300 border-b-4 border-transparent -mb-[2px]"
            >
              Director's Desk
            </a>

            {/* Executive Faculty */}
            <a 
              href="#executive-faculty" 
              onClick={(e) => {
                if (activeView !== 'home') {
                  e.preventDefault();
                  setActiveView('home');
                  setTimeout(() => {
                    document.getElementById('executive-faculty')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }
              }}
              className="py-3 px-2 sm:px-2.5 lg:px-3 text-xs sm:text-sm font-black font-sc uppercase tracking-wider transition-all whitespace-nowrap text-slate-700 hover:text-[#0B2644] hover:border-slate-300 border-b-4 border-transparent -mb-[2px]"
            >
              Faculty
            </a>

            {/* Apply for Admission */}
            <button 
              onClick={() => {
                setActiveView('apply');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className={`py-3 px-2 sm:px-2.5 lg:px-3 text-xs sm:text-sm font-black font-sc uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer flex items-center space-x-1.5 border-b-4 -mb-[2px] ${
                activeView === 'apply'
                  ? 'border-[#7A470E] text-[#7A470E]'
                  : 'border-transparent text-[#7A470E] hover:text-[#0B2644] hover:border-[#7A470E]'
              }`}
            >
              <Award className="w-3.5 h-3.5 text-[#7A470E]" />
              <span>Apply for Admission</span>
            </button>

          </div>
        </div>
      </nav>

      {/* ======================================================== */}
      {/* 4. OFFICIAL ADMISSION PLACEMENT RIBBON (Like Ref Image)   */}
      {/* ======================================================== */}
      <div className="bg-[#0B2644] text-white text-[11px] sm:text-xs py-1.5 px-4 text-center border-b border-[#7A470E]/40 shadow-inner font-sc">
        <span className="font-extrabold tracking-wide text-amber-300">
          Official Prospectus & Calendar:
        </span>{' '}
        <span className="text-white font-medium">
          2025/2026 Executive Admissions into Certificate, Diploma, PGD, Master's & Doctorate in Business Admin, Public Admin, and Accounting & Finance are Open. Hybrid Executive Delivery from Obio Akpa Campus & Uyo Liaison Office.
        </span>{' '}
        <button 
          onClick={() => setActiveView('class-timetable')}
          className="underline font-bold text-emerald-300 hover:text-white transition-colors cursor-pointer ml-1.5"
        >
          View 2025/2026 Class Timetable →
        </button>
      </div>

      {/* ======================================================== */}
      {/* 5. MOBILE MENU DROPDOWN                                  */}
      {/* ======================================================== */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-b-2 border-slate-200 px-4 pt-3 pb-6 space-y-3 animate-in fade-in duration-150">
          <nav className="flex flex-col space-y-2 text-sm font-sc">
            <button 
              onClick={() => { setActiveView('home'); setMobileMenuOpen(false); }}
              className={`text-left py-2 font-bold ${activeView === 'home' ? 'text-[#7A470E]' : 'text-slate-700'}`}
            >
              Home View
            </button>
            <button 
              onClick={() => { setActiveView('class-timetable'); setMobileMenuOpen(false); }}
              className={`text-left py-2 font-bold flex items-center justify-between ${activeView === 'class-timetable' ? 'text-[#7A470E]' : 'text-slate-700'}`}
            >
              <span className="flex items-center space-x-1.5">
                <Calendar className="w-4 h-4 text-[#7A470E]" />
                <span>Class Timetable (2025/2026)</span>
              </span>
              <span className="text-[10px] bg-emerald-100 text-emerald-900 px-1.5 py-0.5 rounded font-mono font-bold">Pioneer Cohort</span>
            </button>
            <button 
              onClick={() => { setActiveView('admission-advert'); setMobileMenuOpen(false); }}
              className={`text-left py-2 font-bold flex items-center justify-between ${activeView === 'admission-advert' ? 'text-[#7A470E]' : 'text-slate-700'}`}
            >
              <span className="flex items-center space-x-1.5">
                <FileText className="w-4 h-4 text-[#7A470E]" />
                <span>Admission Advert (Official Circular)</span>
              </span>
              <span className="text-[10px] bg-amber-100 text-[#0B2644] px-1.5 py-0.5 rounded font-mono font-bold">2025/2026</span>
            </button>
            <div className="py-1">
              <button 
                onClick={() => setProgrammesDropdownOpen(!programmesDropdownOpen)}
                className="w-full py-2 text-slate-700 font-bold flex items-center justify-between cursor-pointer"
              >
                <span>Academic Programmes</span>
                <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${programmesDropdownOpen ? 'rotate-180 text-[#7A470E]' : ''}`} />
              </button>

              {programmesDropdownOpen && (
                <div className="pl-3 py-1 space-y-1.5 border-l-2 border-[#7A470E] my-1">
                  {/* Requirements first in mobile programmes */}
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      if (activeView !== 'home') setActiveView('home');
                      setTimeout(() => {
                        document.getElementById('admissions')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    className="w-full text-left py-1.5 px-2 rounded-lg text-xs font-sc font-bold bg-amber-50 text-[#7A470E] flex items-center justify-between cursor-pointer border border-[#7A470E]/30"
                  >
                    <span className="flex items-center space-x-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>Admission Requirements</span>
                    </span>
                    <span className="text-[9px] text-white bg-[#7A470E] px-1 py-0.2 rounded font-mono font-bold">ALL</span>
                  </button>

                  {/* Scholar Registration in Mobile Menu */}
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setActiveView('enrolment');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-full text-left py-1.5 px-2 rounded-lg text-xs font-sc font-bold bg-blue-50 text-[#0B2644] flex items-center justify-between cursor-pointer border border-[#0B2644]/20"
                  >
                    <span className="flex items-center space-x-1.5">
                      <GraduationCap className="w-3.5 h-3.5 text-[#0B2644]" />
                      <span>Scholar Registration Desk</span>
                    </span>
                    <span className="text-[9px] text-white bg-[#0B2644] px-1 py-0.2 rounded font-mono font-bold">ENROL</span>
                  </button>

                  {CORE_PROGRAMMES_SUBMENU.map((prog) => (
                    <button
                      key={prog.id}
                      onClick={() => handleProgrammeClick(prog.id)}
                      className="w-full text-left py-1.5 px-2 rounded-lg text-xs font-sc font-bold text-[#0B2644] hover:bg-amber-50 flex items-center justify-between cursor-pointer"
                    >
                      <span>{prog.name}</span>
                      <span className="text-[10px] text-white bg-[#0B2644] px-1.5 py-0.5 rounded font-mono font-bold">{prog.code}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      if (activeView !== 'home') setActiveView('home');
                      setTimeout(() => {
                        document.getElementById('programmes')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    className="w-full text-left py-1.5 px-2 text-xs font-sc font-bold text-[#7A470E] hover:underline cursor-pointer"
                  >
                    View All Programmes Catalogue →
                  </button>
                </div>
              )}
            </div>
            {/* LMS Mobile Link */}
            <button
              onClick={() => { setActiveView('lms-student'); setMobileMenuOpen(false); }}
              className={`text-left py-2 font-bold flex items-center justify-between ${activeView === 'lms-student' ? 'text-[#7A470E]' : 'text-slate-700'}`}
            >
              <span className="flex items-center space-x-1.5">
                <GraduationCap className="w-4 h-4 text-[#7A470E]" />
                <span>LMS (Scholar Workspace)</span>
              </span>
              <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-mono font-bold">Active</span>
            </button>
            <a 
              href="#director" 
              onClick={() => setMobileMenuOpen(false)}
              className="py-2 text-slate-700 font-bold"
            >
              Director's Desk
            </a>
            <a 
              href="#executive-faculty" 
              onClick={() => setMobileMenuOpen(false)}
              className="py-2 text-slate-700 font-bold"
            >
              Executive Faculty
            </a>
          </nav>

          <div className="pt-3 border-t border-slate-200 flex flex-col space-y-2">
            <button
              onClick={() => { setActiveView('lms-hub'); setMobileMenuOpen(false); }}
              className="w-full py-3 text-center font-black text-sm bg-gradient-to-r from-[#030F1C] to-[#0B2644] text-amber-300 rounded-xl font-sc shadow-md flex items-center justify-center space-x-2 border border-[#7A470E] cursor-pointer"
            >
              <Lock className="w-4 h-4 text-amber-400" />
              <span>Portal Login</span>
            </button>
            <button
              onClick={() => { setActiveView('apply'); setMobileMenuOpen(false); }}
              className="w-full py-2.5 text-center font-bold text-sm bg-[#7A470E] text-white rounded-xl font-sc shadow-sm cursor-pointer"
            >
              Apply for Admission (₦25,000)
            </button>
            <div className="pt-2 flex items-center justify-between text-xs text-slate-500">
              <span className="font-sc font-medium">Follow AKSU Business School:</span>
              <SocialIconLinks theme="light" iconClassName="w-4 h-4" />
            </div>
          </div>
        </div>
      )}

    </header>
  );
};
